import { Component, NgZone, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { LoadManagerService } from "../loadingOverlay/loadManager.service";
import { LoggerService } from "../logging/logger.service";
import { CompanyOffices, Office, OfficeSelectorService } from "./officeSelector.service";
import { BaseComponent } from "../baseComponent";
import { ModalDialogComponent } from "../modal/modalDialog.component";
import '../../util/string';
import { ApigeeService } from "src/app/auth/apigee/apigee.service";
import { Router } from "@angular/router";

@Pipe({
    name: 'officeSelectorFilterCompany',
    standalone: false
})
export class OfficeSelectorFilterCompany implements PipeTransform {
    transform(companies: CompanyOffices[], text: string): CompanyOffices[] {
        if (!text || text === "") return companies;
        else return companies.filter(x => x.fiscalName.toLowerCase().indexOf(text.toLowerCase()) != -1 || x.offices.some(x => x.selectName.toLowerCase().indexOf(text.toLowerCase()) != -1));
    }
}

@Pipe({
    name: 'officeSelectorFilterOffice',
    standalone: false
})
export class OfficeSelectorFilterOffice implements PipeTransform {
    transform(offices: Office[], text: string): Office[] {
        if (!text || text === "") return offices;
        else return offices.filter(x => x.selectName.toLowerCase().indexOf(text.toLowerCase()) != -1);
    }
}

@Component({
    selector: 'office-selector',
    templateUrl: './officeSelector.component.html',
    styleUrls: ['./officeSelector.component.css'],
    standalone: false
})
export class OfficeSelectorComponent extends BaseComponent implements OnInit, OnDestroy {
    private mSelectedOffice: Office = null;
    public get selectedOfficeName(): string { return this.mSelectedOffice ? this.mSelectedOffice.name.toLowerCase().capitalize() : null; };
    public get selectedOfficeIsUserOffice(): boolean { return this.mSelectedOffice ? this.mSelectedOffice.isUserOffice : true; };
    public companies: CompanyOffices[];
    public dialogLoading: boolean = false;
    public processOffices: boolean = false;
    public searchText: string = null;
    public get canCancelDialog(): boolean { return this.mSelectedOffice != null; };


    @ViewChild(ModalDialogComponent, { static: true }) private dialog: ModalDialogComponent;

    private readonly authTokensUpdatedSubscription: Subscription;
    private readonly selectedOfficeUpdatedSubscription: Subscription;

    constructor(private apigeeService: ApigeeService, private officeSelectorService: OfficeSelectorService, private router: Router,
        zone: NgZone, private loadManagerService: LoadManagerService, private logger: LoggerService) {
        super(zone);

        this.authTokensUpdatedSubscription = this.apigeeService.authTokensUpdated.subscribe((authTokens) => {
            try {
                if (this.apigeeService.apiToken) {
                    if (!this.officeSelectorService.selectedOffice) {
                        this.openDialog();
                    }
                } else {
                    this.officeSelectorService.setSelectedOffice(null);
                }
            } catch (error) {
                this.logger.error(error.error ? error.error.message : error.message);
            }
        });
        this.selectedOfficeUpdatedSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe((x) => {
            this.doAsyncUIChanges(() => this.mSelectedOffice = x);
        });
    }

    public async ngOnInit(): Promise<void> {
        this.mSelectedOffice = this.officeSelectorService.selectedOffice;
        await this.checkOfficeSelected();
    }

    public ngOnDestroy(): void {
        if (this.authTokensUpdatedSubscription) this.authTokensUpdatedSubscription.unsubscribe();
        if (this.selectedOfficeUpdatedSubscription) this.selectedOfficeUpdatedSubscription.unsubscribe();
    }

    private async checkOfficeSelected(): Promise<void> {
        let token = this.apigeeService.apiToken;
        let selectedOffice = this.officeSelectorService.selectedOffice;

        if (!token) {
          this.zone.run(() => this.router.navigate(['login']));
        }
        else {
            if (!selectedOffice) {
                this.openDialog();
            }
            else {
                try {
                    this.loadManagerService.incLoadRequest();
                    let nif = this.apigeeService.userInfo.nameid;
                    this.companies = await this.officeSelectorService.getOfficeList(nif);
                    this.validateRetriesRequestToOffice(nif);

                    if (this.processOffices && !this.companies.some(x => x.offices.some(y => y.id == selectedOffice.id))) {
                        await this.officeSelectorService.setSelectedOffice(null);
                        this.openDialog();
                    }
                } finally {
                    this.loadManagerService.decLoadRequest();
                }
            }
        }
    }

    public async onSelectOffice(event: Event): Promise<void> {
        if (event) event.stopPropagation();
        if (!this.loadManagerService.isLoading) {
            await this.openDialog(false);
        }
    }

    private async openDialog(autoSelectIfSingleOption: boolean = true): Promise<void> {
        try {
            this.dialogLoading = true;
            this.companies = [];
            this.searchText = null;

            this.dialog.open();

            let token = this.apigeeService.apiToken;
            if (token) {
                let nif = this.apigeeService.userInfo.nameid;
                this.companies = await this.officeSelectorService.getOfficeList(nif);
                this.validateRetriesRequestToOffice(nif);

                if (this.processOffices && autoSelectIfSingleOption) {
                    //just one office available
                    let offices = this.companies.reduce((acc, x) => { return acc.concat(x.offices); }, []);
                    if (offices.length == 1) {
                        this.onOfficeSelected(null, offices[0]);
                    }
                }
            }
        } catch (error) {
            this.dialog.close(null);
            throw error;
        } finally {
            this.dialogLoading = false;
        }
    }

    public async onOfficeSelected(event: Event, office: Office): Promise<void> {
        this.loadManagerService.incLoadRequest();
        try {
            this.dialog.close(event);
            let selectedOffice = this.officeSelectorService.selectedOffice;
            if (!selectedOffice || (selectedOffice && selectedOffice.id != office.id)) {
                await this.officeSelectorService.setSelectedOffice(office);
            }
        }
        finally {
            this.loadManagerService.decLoadRequest();
        }
    }

    /**
    * Validar re-intentos de solicitud a la offcina
    * @param fiscalDocNumber Numero de documento fiscal
    */
    private async validateRetriesRequestToOffice(fiscalDocNumber: string) {
        let lsAttemps = sessionStorage.getItem("appAttemps");
        let attemps = lsAttemps ? Number.parseInt(lsAttemps.toString()) : 0;
        let count = 0;
        if (this.companies.length === 0 && attemps > 0) {
            sessionStorage.setItem("appAttemps", "1");

            while (count <= 3 && !this.processOffices) {
                this.companies = await this.officeSelectorService.getOfficeList(fiscalDocNumber);
                this.processOffices = this.companies.length > 0;
                count++;
            }
        }
        else {
            this.processOffices = true;
        }
    }
}
